import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { log } from '@/lib/log'

export type ThemePreference = 'light' | 'dark' | 'system'

const STORAGE_KEY = 'vecos-admin-theme'

interface ThemeContextValue {
  preference: ThemePreference
  setPreference: (next: ThemePreference) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

function read(): ThemePreference {
  // A browser set to block site data throws on access rather than returning null.
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'light' || stored === 'dark') return stored
  } catch (cause) {
    log.exception('Could not read the stored theme', cause)
  }
  return 'system'
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(read)

  useEffect(() => {
    const root = document.documentElement

    // 'system' removes the stamp entirely, which is what lets prefers-color-scheme decide.
    if (preference === 'system') delete root.dataset.theme
    else root.dataset.theme = preference

    try {
      if (preference === 'system') localStorage.removeItem(STORAGE_KEY)
      else localStorage.setItem(STORAGE_KEY, preference)
    } catch (cause) {
      log.exception('Could not store the theme preference', cause)
    }
  }, [preference])

  const setPreference = useCallback((next: ThemePreference) => { setPreferenceState(next) }, [])
  const value = useMemo(() => ({ preference, setPreference }), [preference, setPreference])

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext)
  if (!context) throw new Error('useTheme must be used inside a ThemeProvider')
  return context
}
